import eventBus from '@paybis/frontend-common-lib/src/helpers/eventBus';
import EventNames from '@paybis/frontend-common-lib/src/models/eventNames';
import { captureException } from '@paybis/frontend-common-lib/src/plugins/sentry';
import { getCorrelationId } from '@paybis/frontend-common-lib/src/services/correlation';
import { isStatusCritical } from '@paybis/frontend-common-lib/src/services/error';
import TokenService from '@paybis/frontend-common-lib/src/services/token';
import axiosRetry from 'axios-retry';
import Vue from 'vue';

import router from '@/route/index';
import store from '@/store/index';

import axios from '../../assets/js/services/network';

const ERROR_PAGE_NAME = 'error';
const SERVER_TIMESTAMP_HEADER = 'x-timestamp';
const AUTHORIZATION = 'Authorization';

const getCsrf = () => {
  if (Vue.$isServer) {
    return null;
  }

  let _csrf = null;
  const element = document.head.querySelector('meta[name="csrf-token"]');
  if (element !== null) {
    _csrf = element.content;
  }

  return _csrf;
};

export default () => {
  const instance = axios.create();

  axiosRetry(instance, {
    retries: 1,
    retryDelay: () => {
      return 5000;
    },
  });

  const cancelBucket = {};

  instance.interceptors.request.use(
    config => {
      config.timeout = config.timeout || 30000; // timeout request
      config.headers['X-Correlation-ID'] = getCorrelationId();
      if (TokenService.hasToken()) {
        config.headers[AUTHORIZATION] = `Bearer ${TokenService.getToken()}`;
      }

      config.metadata = { startTime: new Date() };

      const url = config.url;
      if (cancelBucket[url]) {
        // XXX: cancel request for current url
        cancelBucket[url].abort();
      }

      const abortController = new AbortController();
      config.signal = abortController.signal;

      cancelBucket[url] = abortController;

      if (config.method !== 'get' && url.indexOf('/') === 0) {
        // Add _csrf token header for internal yii2 calls
        const _csrf = getCsrf();
        if (_csrf !== null) {
          config.headers['X-CSRF-TOKEN'] = _csrf;
        }
      }

      return config;
    },
    error => Promise.reject(error),
  );

  instance.interceptors.response.use(
    response => {
      if (typeof response.headers[SERVER_TIMESTAMP_HEADER] !== 'undefined') {
        const endTime = new Date();
        const responseDuration = endTime - response.config.metadata.startTime;
        const serverTime = response.headers[SERVER_TIMESTAMP_HEADER] * 1000; // ms

        store.commit('setServerTimeDiff', serverTime + responseDuration - Date.now());
      }

      return response;
    },
    error => {
      if (axios.isCancel(error)) {
        return new Promise(() => { });
      }

      const { config, response } = error;

      // Not all errors have response. For example CORS have not response.
        if (response === undefined) {
            captureException({ error });
            return Promise.reject(error);
        }

      const { url: requestUrl } = config || {};

      store.dispatch('error/setError', response);

      if (store.getters['error/isUnauthorized']) {
          const extra = {
              cookieEnabled: navigator.cookieEnabled,
              token: TokenService.getToken(),
              refreshToken: TokenService.getRefreshToken(),
              cookies: document.cookie,
              errorExtraMessage: 'Error in refresh method for token',
          };

        const { config: eConfig, response: eResponse } = error;
        if (eConfig) {
          extra.config = eConfig;
        }
        if (eResponse) {
          extra.response = eResponse;
        }

        if (requestUrl.indexOf('/jwt/refresh') === -1 && requestUrl.indexOf('/') !== 0) {
          return TokenService
            .requestNewToken()
            .then(({ jwt }) => {
              config.headers[AUTHORIZATION] = `Bearer ${jwt}`;

              return instance.request(config);
            })
            .catch(e => {
              captureException({ error: e, extra });

              throw error;
            });
        }
        captureException({ error, extra });
        eventBus.emit(EventNames.SESSION_TIMEOUT);

        throw error;
      }

        if (isStatusCritical(response.status) && router.history.current.name !== ERROR_PAGE_NAME) {

            const sentryIgnoreErrorStatuses = error.config.sentryIgnoreErrorStatuses || [];
            if (
                sentryIgnoreErrorStatuses.length === 0 ||
                !sentryIgnoreErrorStatuses.includes(error.response.status)
            ) {
                captureException({ error });
            }

            router.push({ name: ERROR_PAGE_NAME });
        }

      Object.assign(error, error.response);
      return Promise.reject(error);
    },
  );

  return instance;
};
